import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { opsService } from '../../services/opsService';

// Internal ops dashboard. Heidi-only, reachable only via direct URL — no nav
// entry and no roleAccess route allowlist. Access is gated by RequireOpsAdmin
// on the route plus the edge function's OPS_ADMIN_EMAILS allowlist.

const STATUS_BADGE = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  trialing: 'bg-amber-100 text-amber-700 border-amber-200',
  past_due: 'bg-red-100 text-red-700 border-red-200',
  unpaid: 'bg-red-100 text-red-700 border-red-200',
  canceled: 'bg-red-50 text-red-600 border-red-100',
  inactive: 'bg-gray-100 text-gray-600 border-gray-200',
};

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

const StatCard = ({ label, value, hint, tone }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
    <p className={`mt-2 text-3xl font-bold ${tone || 'text-slate-900'}`}>{value}</p>
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

const OpsOverview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      const res = await opsService.getOverview();
      if (cancelled) return;
      if (res?.error) setError(res.error.message || 'Failed to load ops stats');
      else setData(res?.data || null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const list = data?.customers || [];
    return list.filter((c) => {
      const matchSearch = !search ||
        c?.companyName?.toLowerCase()?.includes(search.toLowerCase()) ||
        c?.id?.toLowerCase()?.includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || c?.subscriptionStatus === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [data, search, statusFilter]);

  const stats = data?.stats;

  return (
    <>
      <Helmet><title>Ops Dashboard</title></Helmet>
      <div className="min-h-screen bg-slate-50">
        {/* Header — dark navy, Heidi's brand shade */}
        <header className="bg-[#061a2b] text-white">
          <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">SiteProof · Internal</p>
              <h1 className="text-2xl font-bold">Ops Dashboard</h1>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/admin-dashboard"
                className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white transition-colors"
              >
                ← Back to SiteProof
              </Link>
              <p className="text-xs text-slate-400">Read-only · Heidi only</p>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Top stats */}
          <section>
            <h2 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">Snapshot</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard label="Total Customers" value={loading ? '…' : stats?.totalCustomers ?? 0} />
              <StatCard label="Active Sites" value={loading ? '…' : stats?.activeSites ?? 0} tone="text-emerald-600" />
              <StatCard label="Total Users" value={loading ? '…' : stats?.totalUsers ?? 0} />
              <StatCard
                label="Estimated MRR"
                value={loading ? '…' : fmtGBP(stats?.estimatedMRR?.value ?? 0)}
                hint="Estimated — derived from active sites × plan rate"
                tone="text-emerald-600"
              />
              <StatCard label="Trial Customers" value={loading ? '…' : stats?.trialCustomers ?? 0} tone="text-amber-600" />
              <StatCard
                label="Failed Payments"
                value={loading ? '…' : stats?.failedPayments ?? 0}
                hint="past_due + unpaid"
                tone={(stats?.failedPayments ?? 0) > 0 ? 'text-red-600' : 'text-slate-900'}
              />
            </div>
          </section>

          {/* Customer table */}
          <section className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <h2 className="text-sm font-semibold text-slate-900">Customers</h2>
              <div className="flex items-center gap-2">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search company…"
                  className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#061a2b]/30"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#061a2b]/30"
                >
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="trialing">Trialing</option>
                  <option value="past_due">Past due</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="canceled">Canceled</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">Company</th>
                    <th className="text-left px-5 py-3 font-semibold">Status</th>
                    <th className="text-left px-5 py-3 font-semibold">Billing</th>
                    <th className="text-right px-5 py-3 font-semibold">Active Sites</th>
                    <th className="text-right px-5 py-3 font-semibold">Users</th>
                    <th className="text-right px-5 py-3 font-semibold">Est. Monthly</th>
                    <th className="text-left px-5 py-3 font-semibold">Last Activity</th>
                    <th className="text-right px-5 py-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading && (
                    <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400">Loading customers…</td></tr>
                  )}
                  {!loading && filtered.length === 0 && (
                    <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400">No customers match</td></tr>
                  )}
                  {!loading && filtered.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 font-medium text-slate-900">{c.companyName || '—'}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_BADGE[c.subscriptionStatus] || 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                          {c.subscriptionStatus || 'unknown'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-600 capitalize">{c.billingInterval || '—'}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-900">{c.activeSiteCount ?? 0}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-900">{c.userCount ?? 0}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-900">{fmtGBP(c.estimatedMonthlyValue ?? 0)}</td>
                      <td className="px-5 py-3 text-slate-600">{fmtDate(c.lastActivity)}</td>
                      <td className="px-5 py-3 text-right">
                        <button
                          onClick={() => navigate(`/ops/${c.id}`)}
                          className="text-xs font-semibold text-[#061a2b] hover:underline"
                        >
                          View →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    </>
  );
};

export default OpsOverview;
